import { FC, ReactNode, useState } from 'react';
import {
  UserProject,
  UserProjectSubmission,
  UserProjectStatus,
  useDeleteUserProjectSubmissionMutation,
} from '@teachur/http-client';
import { CancelButton } from '@new/common/ui/CancelButton/CancelButton';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { SubmissionDetailModal } from '@new/common/ui/SubmissionDetail/SubmissionDetailModal';
import { toProjectSubmissionDetail as toSubmissionDetailData } from '@new/common/ui/SubmissionDetail/submissionDetail.mappers';
import { getLatestSubmission } from '@new/features/projects-review/utils/projectReviewDrawerHelpers';

interface SubmissionItemProps {
  userProject: UserProject;
  submission: UserProjectSubmission;
  statusBadge: ReactNode;
}

export const SubmissionItem: FC<SubmissionItemProps> = ({
  userProject,
  submission,
  statusBadge,
}) => {
  const deleteSubmissionMutation = useDeleteUserProjectSubmissionMutation();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const latestSubmission = getLatestSubmission(userProject);
  const isLatest = latestSubmission?.id === submission.id;

  const onCancelProject = () => {
    setIsCancelModalOpen(true);
  };

  const onConfirmCancel = () => {
    deleteSubmissionMutation.mutate({
      userProjectId: userProject.id,
      submissionId: submission.id,
    });
    setIsCancelModalOpen(false);
  };

  const canCancel = isLatest && submission.status === UserProjectStatus.Pending;
  const canViewReview =
    submission.status === UserProjectStatus.Approved ||
    submission.status === UserProjectStatus.NotYet;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-(--neutral-black-NB100) bg-(--neutral-white-NW1000) p-4 shadow-md">
        <div className="flex flex-col gap-1 flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {submission.file && (
              <a
                href={submission.file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)! font-medium hover:underline"
              >
                {submission.file.pid || 'View File'}
              </a>
            )}
          </div>
          <span className="[font-size:var(--fs-l5)]! text-(--neutral-white-NW600)!">
            Submitted:{' '}
            {new Date(submission.createdAt).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </span>
          {submission.note && (
            <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)! m-0! italic">
              Note: {submission.note}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          {statusBadge}
          {canViewReview && (
            <button
              onClick={() => setIsReviewModalOpen(true)}
              className="[font-size:var(--fs-l5)]! font-medium text-(--primary-O1000)! cursor-pointer hover:text-(--primary-O1100)! transition-colors bg-transparent border-0 p-0 underline"
            >
              View Review
            </button>
          )}
          {canCancel && (
            <CancelButton
              onClick={onCancelProject}
              disabled={deleteSubmissionMutation.isPending}
              text="Cancel"
            />
          )}
        </div>
      </div>

      <SubmissionDetailModal
        isOpen={isReviewModalOpen}
        data={
          isReviewModalOpen ? toSubmissionDetailData(userProject, submission, statusBadge) : null
        }
        onClose={() => setIsReviewModalOpen(false)}
      />

      <ConfirmationModal
        isOpen={isCancelModalOpen}
        title="Cancel Project"
        description="Are you sure you want to cancel this project submission? This action cannot be undone."
        confirmText="Yes"
        cancelText="No"
        isLoading={deleteSubmissionMutation.isPending}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={onConfirmCancel}
      />
    </>
  );
};
